// @flow

// The Task component manages the different phases and screens of a single task.

import assert from "assert";
import React, { Component } from "react";
import _ from "lodash";

import type { TaskType } from "../../data/tasks";
import type { Result } from "../../data/results";
import type { createSearchType } from "../../data/search";

import FeedbackScreen from "./FeedbackScreen";
import ElicitationScreen from "./ElicitationScreen";
import TimeoutScreen from "./TimeoutScreen";
import TimeProgressBar from "../TimeProgressBar/TimeProgressBar";
import WithTimer from "../WithTimer/WithTimer";
import templates from "../../templates/";
import { Results } from "../../data/results";
import { withCreateSearch } from "../../data/search";

import ReactGA from "react-ga";

import "./Task.css";

type Props = {
  task: TaskType,
  uploadResults: (results: Results) => void,
  advance: (results: Results) => void,
  secondsElapsed: number,
  resetTimer: () => void,
  pauseTimer: () => void,
  resumeTimer: () => void,
  azureToken: string
} & createSearchType;

type State = {
  intervalIndex: number,
  screen: "elicitation" | "timeout" | "post-response" | "feedback",
  results: Results,
  lostFocus: boolean,
  searchesIds: Array<string>
};

class Task extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      intervalIndex: 0,
      screen: "elicitation",
      results: new Results(props.task),
      lostFocus: false,
      searchesIds: []
    };
  }
  componentWillMount() {
    ReactGA.pageview("Task screen");
  }

  componentDidUpdate() {
    if (
      (this.state.screen === "elicitation" ||
        this.state.screen === "post-response") &&
      this.props.secondsElapsed >= this.timeLimit()
    ) {
      this.handleTimeout();
    }
  }

  log(...args) {
    console.log(this.props.secondsElapsed, ...args);
  }

  timeLimit = () => {
    return this.props.task.responseTimesInSeconds[this.state.intervalIndex];
  };

  timeRemaining = () => {
    return this.timeLimit() - this.props.secondsElapsed;
  };

  currentResult(): ?Result {
    return this.state.results.get(this.timeLimit());
  }

  isFinalInterval() {
    return (
      this.state.intervalIndex ===
      this.props.task.responseTimesInSeconds.length - 1
    );
  }

  template() {
    const templateName = this.props.task.project.template;
    const template = templates[templateName];
    assert.ok(template, `Didn't find template ${JSON.stringify(template)}`);
    return template;
  }

  handleTimeout = () => {
    ReactGA.event({ category: "Task", action: "Timeout" });
    this.props.pauseTimer();
    this.setState({ screen: "timeout" });
    const results = this.addResult({
      kind: "timeout",
      lostFocus: this.state.lostFocus,
      secondsLimit: this.timeLimit(),
      secondsElapsed: this.props.secondsElapsed,
      searchesIds: this.state.searchesIds
    });
    this.props.uploadResults(results);
  };

  handleLostFocus = () => {
    if (!this.state.lostFocus) {
      ReactGA.event({ category: "Task", action: "Lost Focus" });
      this.setState({ lostFocus: true });
    }
  };

  handleSearch = (query: string) => {
    ReactGA.event({ category: "Task", action: "Search", label: query });
    this.props
      .createSearch({
        variables: {
          query: query,
          secondsElapsed: _.round(this.props.secondsElapsed, 5)
        }
      })
      .then(response => {
        this.setState({
          searchesIds: [
            ...this.state.searchesIds,
            response.data.createSearch.id
          ]
        });
      });
  };

  handleSubmit = (data: { confidence: number }) => {
    ReactGA.event({
      category: "Task",
      action: "Submit",
      value: data.confidence
    });
    this.props.pauseTimer();
    if (this.currentResult()) {
      throw new Error(
        `Already got response ${JSON.stringify(
          this.currentResult()
        )}, got additional response ${JSON.stringify(data)}`
      );
    }
    const result = {
      kind: "response",
      secondsElapsed: this.props.secondsElapsed,
      secondsLimit: this.timeLimit(),
      lostFocus: this.state.lostFocus,
      searchesIds: this.state.searchesIds,
      ...data
    };

    const results = this.addResult(result);
    if (this.isFinalInterval()) {
      this.props.uploadResults(results);
      this.advanceToFeedback();
    } else {
      this.handleMoreTimeClick();
    }
  };

  addResult(result: Result) {
    this.log("Adding result", result);

    const results = this.state.results.add(result);
    this.setState({
      results
    });
    return results;
  }

  handleContinue = () => {
    ReactGA.event({ category: "Task", action: "Continue" });
    this.props.advance(this.state.results);
  };

  handleMoreTimeClick = () => {
    assert.ok(!this.isFinalInterval());
    ReactGA.event({
      category: "Task",
      action: "More Time",
      value: this.state.intervalIndex
    });
    this.setState({
      intervalIndex: this.state.intervalIndex + 1,
      lostFocus: false,
      searchesIds: [],
      screen: "post-response"
    });
    this.props.resumeTimer();
  };

  advanceToFeedback = () => {
    this.setState({
      screen: "feedback"
    });
  };

  advanceToElicitation = () => {
    this.setState({ screen: "elicitation" });
  };

  renderScreen() {
    const screen = this.state.screen;

    const template = this.template();
    if (screen === "elicitation" || screen === "post-response") {
      const modalVisible = screen === "post-response";
      return (
        <ElicitationScreen
          task={this.props.task}
          template={template}
          submit={this.handleSubmit}
          lostFocus={this.handleLostFocus}
          modalVisible={modalVisible}
          closeModal={this.advanceToElicitation}
          secondsRemaining={this.timeRemaining()}
          search={this.handleSearch}
          azureToken={this.props.azureToken}
        />
      );
    } else if (screen === "timeout") {
      return <TimeoutScreen continue={this.handleContinue} />;
    } else if (screen === "feedback") {
      const results = this.state.results;
      if (
        !results ||
        !_.every(results.toArray(), result => result.kind === "response")
      ) {
        throw new Error(
          "Should only show feedback screen for responses, not timeouts"
        );
      }
      const question = template.task.renderQuestion(this.props.task);
      return (
        <FeedbackScreen
          question={question}
          results={results}
          template={template}
          answer={this.props.task.answer}
          continue={this.handleContinue}
        />
      );
    } else {
      throw new Error(`Task: Unknown screen type: ${screen}`);
    }
  }

  render() {
    return (
      <div className="Task">
        <div className="Task-Progressbar">
          <TimeProgressBar
            max={this.timeLimit()}
            now={this.props.secondsElapsed}
          />
        </div>
        <div className="Task-Screen">{this.renderScreen()}</div>
      </div>
    );
  }
}

export default withCreateSearch(WithTimer(Task));



// WEBPACK FOOTER //
// ./src/components/Task/Task.js